import type { FC, ReactNode } from 'react'
import type { CaseStudy } from '@/constants/full-service'

import { useId, useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/generals/modal'
import { Reveal } from '@/components/generals/reveal'
import { Wordmark } from '@/components/generals/wordmark'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'
import { caseStudies } from '@/constants/full-service'

interface FactsProps {
    study: CaseStudy
    onRead: () => void
    tone?: 'light' | 'card'
}

const Facts: FC<FactsProps> = ({ study, onRead, tone = 'light' }): ReactNode => {
    const rows: Array<[string, ReactNode]> = [
        ['Industry', study.industry],
        ['Problems solved', (
            <ul key='problems' className='flex flex-col gap-1'>
                {study.problems.map(problem => (
                    <li key={problem}>
                        {problem}
                    </li>
                ))}
            </ul>
        )],
        ['Location', study.location],
        ['Working together since', study.since],
        ['Website', study.website]
    ]

    return (
        <div className={cn('flex flex-col', tone === 'card' && 'rounded-[20px] bg-white p-6 text-ink shadow-[0_24px_48px_-30px_rgba(0,0,0,0.6)]')}>
            <dl className='flex flex-col divide-y divide-hairline'>
                {rows.map(([label, value]) => (
                    <div key={label} className='grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-4 py-3 text-[14px] leading-relaxed first:pt-0'>
                        <dt className='font-medium text-slate'>
                            {label}
                        </dt>

                        <dd className='text-ink'>
                            {value}
                        </dd>
                    </div>
                ))}
            </dl>

            <button
                type='button'
                onClick={onRead}
                className='group mt-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-ink px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-[#26262b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
            >
                Read story
                <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' aria-hidden='true' />
            </button>
        </div>
    )
}

const StoryDialog: FC<{ study: CaseStudy | null, onClose: () => void }> = ({ study, onClose }): ReactNode => {
    const id = useId()
    const { openContact } = useContact()

    return (
        <Modal open={study !== null} onClose={onClose} labelledBy={`${id}-title`} className='sm:max-w-[620px]'>
            {study && (
                <article className='flex flex-col gap-7 px-6 pb-8 pt-9 sm:px-9 sm:pb-9'>
                    <div className='flex flex-col gap-4 pr-8'>
                        <Wordmark name={study.company} className='text-[22px] text-ink' />

                        <h2 id={`${id}-title`} className='text-[26px] font-medium leading-tight tracking-[-0.03em]'>
                            How {study.company} runs on FlowCraft
                        </h2>

                        <p className='text-[14px] text-slate'>
                            {study.industry}
                            <span aria-hidden='true' className='mx-2 text-[#c9c9d2]'>/</span>
                            {study.location}
                            <span aria-hidden='true' className='mx-2 text-[#c9c9d2]'>/</span>
                            Since {study.since}
                        </p>
                    </div>

                    {study.quote && (
                        <blockquote className='rounded-2xl bg-mist px-5 py-4 text-[16px] leading-relaxed text-ink'>
                            &ldquo;{study.quote}&rdquo;

                            <footer className='mt-2 text-[13.5px] text-slate'>
                                {study.person}, {study.title}
                            </footer>
                        </blockquote>
                    )}

                    <div className='flex flex-col gap-2'>
                        <h3 className='text-[13px] font-semibold uppercase tracking-[0.08em] text-silver'>
                            The challenge
                        </h3>

                        <p className='text-[15.5px] leading-relaxed text-graphite'>
                            {study.story.challenge}
                        </p>
                    </div>

                    <div className='flex flex-col gap-2'>
                        <h3 className='text-[13px] font-semibold uppercase tracking-[0.08em] text-silver'>
                            What we built
                        </h3>

                        <p className='text-[15.5px] leading-relaxed text-graphite'>
                            {study.story.solution}
                        </p>
                    </div>

                    <div className='flex flex-col gap-3'>
                        <h3 className='text-[13px] font-semibold uppercase tracking-[0.08em] text-silver'>
                            Results
                        </h3>

                        <ul className='flex flex-col gap-2.5'>
                            {study.story.results.map(result => (
                                <li key={result} className='flex items-start gap-3 text-[15.5px] text-ink'>
                                    <span className='mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-mint text-forest'>
                                        <Check className='size-3' aria-hidden='true' />
                                    </span>
                                    {result}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <PillButton
                        chevron
                        className='w-fit'
                        onClick={() => {
                            onClose()
                            openContact({ intent: 'contact', message: `We read the ${study.company} story and would like something similar.` })
                        }}
                    >
                        Talk to our team
                    </PillButton>
                </article>
            )}
        </Modal>
    )
}

export const CaseStudies: FC = (): ReactNode => {
    const [reading, setReading] = useState<CaseStudy | null>(null)
    const [featured, ...others] = caseStudies

    return (
        <section id='customers' aria-labelledby='customers-title' className='border-t border-hairline py-24 md:py-32'>
            <div className='fc-container'>
                <Reveal>
                    <h2 id='customers-title' className='max-w-[880px] text-[clamp(2.25rem,4.6vw,4rem)] font-medium leading-[1.04] tracking-[-0.04em]'>
                        <span className='text-ink'>We&apos;ve worked with hundreds of businesses</span>{' '}
                        <span className='text-silver'>to run their production workflows.</span>
                    </h2>
                </Reveal>

                <Reveal delay={100} className='mt-14 md:mt-16'>
                    <article aria-label={`${featured.company} case study`} className='relative isolate grid gap-10 overflow-hidden rounded-[28px] bg-forest p-6 text-white sm:p-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)] lg:gap-16 lg:p-14'>
                        <div aria-hidden='true' className='pointer-events-none absolute -left-40 -top-40 -z-10 size-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(197,241,216,0.18),transparent)]' />

                        <div className='flex flex-col justify-between gap-12'>
                            <div className='flex flex-col gap-8'>
                                <Wordmark name={featured.company} className='text-[24px] text-white' />

                                <blockquote className='max-w-[640px] text-[clamp(1.5rem,2.4vw,2rem)] leading-[1.25] tracking-[-0.02em]'>
                                    &ldquo;{featured.quote}&rdquo;
                                </blockquote>

                                <p className='flex flex-wrap items-center gap-x-2 text-[14px] font-medium'>
                                    <span>{featured.person}</span>
                                    <span aria-hidden='true' className='text-white/40'>/</span>
                                    <span className='text-mint/80'>{featured.title}</span>
                                </p>
                            </div>

                            {featured.metrics && (
                                <dl className='grid gap-6 border-t border-white/15 pt-8 sm:grid-cols-3'>
                                    {featured.metrics.map(metric => (
                                        <div key={metric.label} className='flex flex-col gap-2'>
                                            <dt className='text-[13.5px] text-white/65'>
                                                {metric.label}
                                            </dt>

                                            <dd className='text-[clamp(1.75rem,2.6vw,2.375rem)] font-medium leading-none tracking-[-0.03em] text-mint'>
                                                {metric.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                        </div>

                        <Facts study={featured} tone='card' onRead={() => setReading(featured)} />
                    </article>
                </Reveal>

                <div className='mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-6'>
                    {others.map((study, index) => (
                        <Reveal
                            key={study.company}
                            delay={(index % 3) * 80}
                            className={cn(
                                'flex flex-col gap-7 rounded-[24px] bg-white p-6 ring-1 ring-inset ring-[#e9e9ee] transition-shadow duration-300 hover:shadow-[0_24px_48px_-32px_rgba(14,14,14,0.35)] sm:p-7',
                                index < 2 ? 'lg:col-span-3' : 'lg:col-span-2',
                                index === others.length - 1 && 'md:col-span-2 lg:col-span-2'
                            )}
                        >
                            <Wordmark name={study.company} className='text-[20px] text-ink' />
                            <Facts study={study} onRead={() => setReading(study)} />
                        </Reveal>
                    ))}
                </div>
            </div>

            <StoryDialog study={reading} onClose={() => setReading(null)} />
        </section>
    )
}
